import { ensureNotNil } from '@studio/js-utils';
import { useLatestFn, useMount } from '@studio/react-utils/hooks';
import { useContext } from 'react';
import { Context } from './context.js';
import type { ListenerFn, Wiring } from './types.js';

/**
 * Register a command listener.
 *
 * @param listenerFn - Listener function. This function will be stabilized.
 */
export const useListener = (listenerFn: ListenerFn): void => {
  const wiring = useWiring();
  const latestFn = useLatestFn(listenerFn);

  useMount(() => {
    wiring.add(latestFn);

    return () => {
      wiring.remove(latestFn);
    };
  });
};

/**
 * Exposes a hook to allow an observer to notify command listeners.
 *
 * @returns Notifier function.
 */
export const useNotifier = (): Wiring['notify'] => useWiring().notify;

/** @internal */
export function useWiring(): Wiring {
  return ensureNotNil(useContext(Context));
}
